import { create } from 'zustand';

import { selectIsPremium, useSession } from '../session';
import { useUI } from '../ui';
import {
  BUILDINGS,
  OFFLINE_CAP_HOURS,
  SIGNUP_PROMPT,
  UPGRADES,
  buildingCost,
  computeCps,
  computePerCorrect,
  isUnlocked,
} from './gameConfig';

// One key holds everything: coins, gems, owned helpers, upgrades, settings, prompt flags.
const SAVE_KEY = 'brassflows_game_v2';
const LEGACY_KEY = 'brassflows_idle_v1';
const DEFAULT_SETTINGS = { showHelpers: true, coinPops: true, volume: 50, theme: 'paper' };

const blank = () => ({
  coins: 0,
  gems: 0,
  totalCoins: 0,
  totalGems: 0,
  correct: 0,
  wrong: 0,
  scaleSessions: 0,
  owned: {},
  bought: {},
  settings: { ...DEFAULT_SETTINGS },
  prompts: { signup: false },
});

const read = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch (e) {
    return null;
  }
};

// Keep only what still exists (old saves had helpers that were cut)
const clean = (raw) => {
  const base = blank();
  const owned = {};
  BUILDINGS.forEach((b) => {
    if (raw.owned && raw.owned[b.id] > 0) owned[b.id] = raw.owned[b.id];
  });
  const bought = {};
  UPGRADES.forEach((u) => {
    if (raw.bought && raw.bought[u.id]) bought[u.id] = true;
  });
  return {
    ...base,
    coins: raw.coins || 0,
    gems: raw.gems || 0,
    totalCoins: raw.totalCoins || 0,
    totalGems: raw.totalGems || 0,
    correct: raw.correct || 0,
    wrong: raw.wrong || 0,
    scaleSessions: raw.scaleSessions || 0,
    owned,
    bought,
    settings: { ...DEFAULT_SETTINGS, ...(raw.settings || {}) },
    prompts: { ...base.prompts, ...(raw.prompts || {}) },
  };
};

const load = () => {
  const raw = read(SAVE_KEY) || read(LEGACY_KEY); // v1 saves migrate automatically
  if (!raw) {
    const s = blank();
    const opts = read('options');
    if (opts && typeof opts.volume === 'number') s.settings.volume = opts.volume;
    return { ...s, pendingOffline: 0 };
  }
  return {
    ...clean(raw),
    // seconds away. Not applied until we know if this is a Premium account (see claimOffline)
    pendingOffline: Math.max(0, (Date.now() - (raw.savedAt || Date.now())) / 1000),
  };
};

const withDerived = (s) => ({
  ...s,
  cps: computeCps(s.owned, s.bought),
  perCorrect: computePerCorrect(s.bought),
});

export const useIdleStore = create(() => withDerived(load()));
const get = () => useIdleStore.getState();

// merge a patch and recompute cps / perCorrect
const update = (patch) => {
  const s = { ...get(), ...patch };
  useIdleStore.setState({
    ...patch,
    cps: computeCps(s.owned, s.bought),
    perCorrect: computePerCorrect(s.bought),
  });
};

export const useIdle = (selector = (s) => s) => useIdleStore(selector);
export const getIdleState = () => useIdleStore.getState();

/* ---------- Persistence ---------- */
const serialize = (s) => ({
  coins: s.coins,
  gems: s.gems,
  totalCoins: s.totalCoins,
  totalGems: s.totalGems,
  correct: s.correct,
  wrong: s.wrong,
  scaleSessions: s.scaleSessions,
  owned: s.owned,
  bought: s.bought,
  settings: s.settings,
  prompts: s.prompts,
});

// Saving stays off until offline earnings are claimed, so the "last seen" time isn't overwritten
let saveEnabled = false;
const save = () => {
  if (!saveEnabled) return;
  try {
    localStorage.setItem(SAVE_KEY, JSON.stringify({ ...serialize(get()), savedAt: Date.now() }));
  } catch (e) {
    // ignore storage errors
  }
};

export const exportSave = () => serialize(get());
export const importSave = (raw) => {
  update(clean(raw));
  save();
};

/* ---------- Offline earnings ---------- */
let claimed = false;
export const claimOffline = (isPremium) => {
  if (claimed) return null;
  claimed = true;

  const s = get();
  const capHours = isPremium ? OFFLINE_CAP_HOURS.premium : OFFLINE_CAP_HOURS.free;
  const seconds = Math.min(s.pendingOffline, capHours * 3600);
  const gain = s.cps * seconds;

  update({ coins: s.coins + gain, totalCoins: s.totalCoins + gain, pendingOffline: 0 });
  saveEnabled = true;
  save();
  try {
    localStorage.removeItem(LEGACY_KEY);
  } catch (e) {
    // ignore
  }
  return { gain, seconds, capped: s.pendingOffline > capHours * 3600, capHours };
};
// If auth never answers, don't leave saving switched off forever
setTimeout(() => claimOffline(false), 8000);

/* ---------- Guest -> account nudge ---------- */
const checkMilestone = () => {
  const s = get();
  if (s.prompts.signup) return;
  const session = useSession.getState();
  if (!session.ready || session.user) return;
  const hired = BUILDINGS.reduce((n, b) => n + (s.owned[b.id] || 0), 0);
  if (s.totalCoins >= SIGNUP_PROMPT.coins || hired >= SIGNUP_PROMPT.helpers) {
    update({ prompts: { ...s.prompts, signup: true } });
    useUI.getState().openSignup('milestone');
    save();
  }
};

/* ---------- Actions ---------- */
const coinListeners = new Set();
export const onCoin = (l) => {
  coinListeners.add(l);
  return () => {
    coinListeners.delete(l);
  };
};

export const awardCorrect = () => {
  const s = get();
  const amount = s.perCorrect;
  update({ coins: s.coins + amount, totalCoins: s.totalCoins + amount, correct: s.correct + 1 });
  coinListeners.forEach((l) => l({ amount }));
  checkMilestone();
};

export const recordWrong = () => update({ wrong: get().wrong + 1 });

const gemListeners = new Set();
export const onGem = (l) => {
  gemListeners.add(l);
  return () => {
    gemListeners.delete(l);
  };
};

// Call when a player plays an ENTIRE scale correctly in one run: pays 1 gem
// and fires the gem-drop animation. Returns the gems paid out.
export const awardScaleGem = (amount = 1) => {
  const s = get();
  update({ gems: s.gems + amount, totalGems: s.totalGems + amount });
  save();
  gemListeners.forEach((l) => l({ amount }));
  return amount;
};

// Call when a player finishes a Scales session. Gems are paid per perfect run
// (awardScaleGem), so this only counts the session.
export const completeScaleSession = () => {
  const s = get();
  update({ scaleSessions: s.scaleSessions + 1 });
  save();
  return 0;
};

export const buyBuilding = (id, n = 1) => {
  const b = BUILDINGS.find((x) => x.id === id);
  if (!b) return false;

  // The gate lives here, not in the UI, so no screen can sell around it.
  if (b.premium && !selectIsPremium(useSession.getState())) {
    useUI.getState().openPricing(`${b.name} is a Premium helper.`);
    return false;
  }

  const s = get();
  const owned = s.owned[id] || 0;
  const cost = buildingCost(b, owned, n);
  if (s.coins < cost) return false;

  update({ coins: s.coins - cost, owned: { ...s.owned, [id]: owned + n } });
  checkMilestone();
  save();
  return true;
};

export const buyUpgrade = (id) => {
  const u = UPGRADES.find((x) => x.id === id);
  const s = get();
  if (!u || s.bought[id] || !isUnlocked(u, s)) return false;

  const bought = { ...s.bought, [id]: true };
  if (u.currency === 'gems') {
    if (s.gems < u.cost) return false;
    const patch = { gems: s.gems - u.cost, bought };
    if (u.kind === 'theme') patch.settings = { ...s.settings, theme: u.value }; // equip on purchase
    update(patch);
  } else {
    if (s.coins < u.cost) return false;
    update({ coins: s.coins - u.cost, bought });
  }
  save();
  return true;
};

export const setSetting = (key, value) => {
  update({ settings: { ...get().settings, [key]: value } });
  if (key === 'volume') {
    // keep the Options panel's own copy of volume in step
    try {
      const o = JSON.parse(localStorage.getItem('options'));
      if (o) localStorage.setItem('options', JSON.stringify({ ...o, volume: value }));
    } catch (e) {
      // ignore
    }
  }
  save();
};

export const spendGems = (n) => {
  const s = get();
  if (s.gems < n) return false;
  update({ gems: s.gems - n });
  save();
  return true;
};

export const resetIdleGame = () => {
  const { settings, prompts } = get();
  update({ ...blank(), settings, prompts });
  save();
};

/* ---------- Loop ---------- */
let lastTick = Date.now();
setInterval(() => {
  const now = Date.now();
  const dt = (now - lastTick) / 1000; // delta time, so throttled background tabs stay honest
  lastTick = now;
  const s = get();
  if (s.cps > 0) {
    const gain = s.cps * dt;
    useIdleStore.setState({ coins: s.coins + gain, totalCoins: s.totalCoins + gain });
  }
  if (!s.prompts.signup) checkMilestone();
}, 100);

setInterval(save, 5000);
window.addEventListener('beforeunload', save);
document.addEventListener('visibilitychange', () => {
  if (document.hidden) save();
});